import { useEffect, useState } from 'react'
import { api, apiForm, entrarGo } from '../api'
import {
  Card, Button, Form, Input, Select, Switch, Tag, Empty, Spin, Space, Typography, Drawer,
  message, Tabs, DatePicker, Row, Col, Upload, Checkbox, Divider, Badge
} from 'antd'
import { PlusOutlined, UserOutlined, PhoneOutlined, FileTextOutlined, UploadOutlined, LinkOutlined, FilePdfOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import useIsMobile from '../hooks/useIsMobile'
import { CATEGORIAS_DOC, categoriaInfo, ListaDocumentos } from './documentosComun'
import FragmentadorNominas from './FragmentadorNominas'

const { Title, Text } = Typography

const CAMPOS_FECHA = ['fecha_nacimiento', 'fecha_alta', 'fecha_baja']

export default function Trabajadores() {
  const isMobile = useIsMobile()
  const [trabajadores, setTrabajadores] = useState([])
  const [usuarios, setUsuarios] = useState([])
  const [cargando, setCargando] = useState(true)
  const [abierto, setAbierto] = useState(false)
  const [editando, setEditando] = useState(null)
  const [tab, setTab] = useState('ficha')
  const [guardando, setGuardando] = useState(false)
  const [documentos, setDocumentos] = useState([])
  const [cargandoDocs, setCargandoDocs] = useState(false)
  const [archivo, setArchivo] = useState(null)
  const [subiendo, setSubiendo] = useState(false)
  const [fragmentador, setFragmentador] = useState(false)
  const [form] = Form.useForm()
  const [formDoc] = Form.useForm()

  useEffect(() => { cargar() }, [])

  const cargar = async () => {
    try {
      const [t, u] = await Promise.all([api('/trabajadores'), api('/usuarios')])
      setTrabajadores(t)
      setUsuarios(u)
    } catch (err) {
      message.error(err.message || 'Error cargando trabajadores')
    }
    setCargando(false)
  }

  const cargarDocumentos = async (id) => {
    setCargandoDocs(true)
    try { setDocumentos(await api(`/trabajadores/${id}/documentos`)) } catch (err) { message.error(err.message) }
    setCargandoDocs(false)
  }

  const abrirNuevo = () => {
    setEditando(null)
    form.resetFields()
    form.setFieldsValue({ activo: true })
    setDocumentos([])
    setTab('ficha')
    setAbierto(true)
  }

  const abrirFicha = (t, pestana = 'ficha') => {
    setEditando(t)
    form.resetFields()
    const valores = { ...t }
    CAMPOS_FECHA.forEach(c => { valores[c] = t[c] ? dayjs(t[c]) : null })
    form.setFieldsValue(valores)
    setTab(pestana)
    formDoc.resetFields()
    formDoc.setFieldsValue({ categoria: 'nomina', avisar: true, requiere_firma: true })
    setArchivo(null)
    cargarDocumentos(t.id)
    setAbierto(true)
  }

  const cerrar = () => {
    setAbierto(false)
    setEditando(null)
  }

  const guardar = async (values) => {
    setGuardando(true)
    try {
      const datos = { ...values }
      CAMPOS_FECHA.forEach(c => { datos[c] = values[c] ? values[c].format('YYYY-MM-DD') : null })
      if (datos.usuario_id === undefined) datos.usuario_id = null
      const guardado = editando
        ? await api(`/trabajadores/${editando.id}`, { method: 'PUT', body: JSON.stringify(datos) })
        : await api('/trabajadores', { method: 'POST', body: JSON.stringify(datos) })
      message.success(editando ? 'Ficha guardada' : 'Trabajador dado de alta')
      await cargar()
      if (!editando) abrirFicha(guardado, 'documentos')
      else setEditando(guardado)
    } catch (err) {
      message.error(err.message || 'Error guardando')
    }
    setGuardando(false)
  }

  const subir = async (values) => {
    if (!archivo) { message.error('Elige un archivo'); return }
    setSubiendo(true)
    try {
      const fd = new FormData()
      fd.append('archivo', archivo)
      fd.append('categoria', values.categoria)
      if (values.nombre) fd.append('nombre', values.nombre)
      fd.append('avisar', values.avisar ? 'true' : 'false')
      fd.append('requiere_firma', values.requiere_firma ? 'true' : 'false')
      const r = await apiForm(`/trabajadores/${editando.id}/documentos`, fd)
      const avisos = {
        enviado: 'y se le ha avisado por correo',
        sin_email: '— no tiene correo personal en la ficha, no se le ha avisado',
        de_baja: '— está de baja, no se le ha avisado',
        error: '— el correo de aviso ha fallado',
        smtp_no_configurado: '— el correo de la app no está configurado',
        no_pedido: '',
      }
      message.success(`Documento subido ${avisos[r.aviso] || ''}`)
      formDoc.setFieldsValue({ nombre: '' })
      setArchivo(null)
      cargarDocumentos(editando.id)
      cargar()
    } catch (err) {
      message.error(err.message || 'Error subiendo el documento')
    }
    setSubiendo(false)
  }

  const borrarDoc = async (doc) => {
    try {
      await api(`/documentos-trabajador/${doc.id}`, { method: 'DELETE' })
      message.success('Documento eliminado')
      cargarDocumentos(editando.id)
      cargar()
    } catch (err) {
      message.error(err.message)
    }
  }

  const pedirFirma = async (doc, pedir) => {
    try {
      await api(`/documentos-trabajador/${doc.id}/pedir-firma`, { method: 'PUT', body: JSON.stringify({ requiere_firma: pedir }) })
      message.success(pedir ? 'Se le pide la firma: hasta que firme no podrá descargarlo' : 'Ya no se le pide firma')
      cargarDocumentos(editando.id)
      cargar()
    } catch (err) {
      message.error(err.message)
    }
  }

  const go = async () => {
    try {
      const r = await api('/auth/go', { method: 'POST', body: JSON.stringify({ usuario_id: editando.usuario_id }) })
      entrarGo(r.token)
    } catch (err) {
      message.error(err.message)
    }
  }

  // Usuarios que se pueden vincular: los libres y el que ya tiene este trabajador
  const usuariosLibres = usuarios.filter(u => !trabajadores.some(t => t.usuario_id === u.id && t.id !== editando?.id))

  const fichaForm = (
    <Form form={form} layout="vertical" onFinish={guardar} size={isMobile ? 'middle' : 'small'}>
      <Divider titlePlacement="left" style={{ marginTop: 0 }}>Datos personales</Divider>
      <Row gutter={10}>
        <Col span={isMobile ? 24 : 12}><Form.Item label="Nombre" name="nombre" rules={[{ required: true, message: 'Obligatorio' }]}><Input /></Form.Item></Col>
        <Col span={isMobile ? 24 : 12}><Form.Item label="Apellidos" name="apellidos"><Input /></Form.Item></Col>
        <Col span={12}><Form.Item label="DNI / NIE" name="nif_nie"><Input /></Form.Item></Col>
        <Col span={12}><Form.Item label="Nº Seguridad Social" name="naf"><Input /></Form.Item></Col>
        <Col span={12}><Form.Item label="Fecha de nacimiento" name="fecha_nacimiento"><DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} /></Form.Item></Col>
        <Col span={12}><Form.Item label="Nacionalidad" name="nacionalidad"><Input /></Form.Item></Col>
      </Row>

      <Divider titlePlacement="left">Contacto <Text type="secondary" style={{ fontSize: '11px', fontWeight: 'normal' }}>(el trabajador también puede cambiarlo)</Text></Divider>
      <Row gutter={10}>
        <Col span={12}><Form.Item label="Teléfono" name="telefono"><Input /></Form.Item></Col>
        <Col span={12}><Form.Item label="Correo personal" name="email_personal" rules={[{ type: 'email', message: 'Correo no válido' }]} extra="Aquí le llegan los avisos de documentos nuevos"><Input /></Form.Item></Col>
        <Col span={24}><Form.Item label="Dirección" name="direccion"><Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} /></Form.Item></Col>
        <Col span={24}><Form.Item label="Cuenta bancaria (IBAN)" name="iban"><Input placeholder="ES00 0000 0000 0000 0000 0000" /></Form.Item></Col>
        <Col span={isMobile ? 24 : 10}><Form.Item label="Contacto de emergencia" name="contacto_emergencia_nombre"><Input placeholder="Nombre" /></Form.Item></Col>
        <Col span={isMobile ? 12 : 7}><Form.Item label="Teléfono" name="contacto_emergencia_telefono"><Input /></Form.Item></Col>
        <Col span={isMobile ? 12 : 7}><Form.Item label="Relación" name="contacto_emergencia_relacion"><Input placeholder="Hermano, pareja..." /></Form.Item></Col>
      </Row>

      <Divider titlePlacement="left">Contrato</Divider>
      <Row gutter={10}>
        <Col span={12}><Form.Item label="Puesto" name="puesto"><Input placeholder="Peón agrícola" /></Form.Item></Col>
        <Col span={12}><Form.Item label="Tipo de contrato" name="tipo_contrato"><Input placeholder="Indefinido, fijo discontinuo..." /></Form.Item></Col>
        <Col span={12}><Form.Item label="Fecha de alta" name="fecha_alta"><DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} /></Form.Item></Col>
        <Col span={12}><Form.Item label="Fecha de baja" name="fecha_baja"><DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} /></Form.Item></Col>
      </Row>

      <Divider titlePlacement="left">Acceso a la aplicación</Divider>
      <Form.Item label="Usuario con el que entra" name="usuario_id" extra="Con su usuario verá «Mi perfil»: sus datos y sus documentos. Los usuarios se crean en Usuarios.">
        <Select allowClear placeholder="Sin acceso a la aplicación" options={usuariosLibres.map(u => ({ value: u.id, label: `${u.nombre} (${u.email})` }))} />
      </Form.Item>
      <Form.Item label="Activo" name="activo" valuePropName="checked">
        <Switch checkedChildren="Sí" unCheckedChildren="No" />
      </Form.Item>

      <Button type="primary" block loading={guardando} onClick={() => form.submit()}>
        {editando ? 'Guardar ficha' : 'Dar de alta'}
      </Button>
    </Form>
  )

  const documentosTab = editando && (
    <>
      <Card size="small" style={{ borderRadius: '10px', background: '#f6ffed', marginBottom: '12px' }}>
        <Form form={formDoc} layout="vertical" onFinish={subir} initialValues={{ categoria: 'nomina', avisar: true, requiere_firma: true }}
          onValuesChange={(cambio) => { if (cambio.categoria) formDoc.setFieldsValue({ requiere_firma: categoriaInfo(cambio.categoria).firma }) }}>
          <Form.Item label="Tipo de documento" name="categoria" style={{ marginBottom: '8px' }}>
            <Select options={CATEGORIAS_DOC.map(c => ({ value: c.value, label: c.label }))} />
          </Form.Item>
          <Form.Item label="Nombre (opcional)" name="nombre" style={{ marginBottom: '8px' }} extra="Si lo dejas vacío se usa el nombre del archivo">
            <Input placeholder="Ej.: Nómina octubre 2026" />
          </Form.Item>
          <Upload
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            maxCount={1}
            beforeUpload={(f) => { setArchivo(f); return false }}
            onRemove={() => setArchivo(null)}
            fileList={archivo ? [{ uid: '1', name: archivo.name, status: 'done' }] : []}
          >
            <Button icon={<UploadOutlined />}>Elegir archivo (PDF o foto)</Button>
          </Upload>
          <Form.Item name="requiere_firma" valuePropName="checked" style={{ margin: '8px 0 0' }}>
            <Checkbox>Pedirle que lo firme (no podrá descargarlo hasta firmar)</Checkbox>
          </Form.Item>
          <Form.Item name="avisar" valuePropName="checked" style={{ margin: '0 0 8px' }}>
            <Checkbox>Avisarle por correo</Checkbox>
          </Form.Item>
          <Button type="primary" block loading={subiendo} onClick={() => formDoc.submit()} style={{ background: '#2E7D32' }}>
            Subir documento
          </Button>
        </Form>
      </Card>
      {cargandoDocs
        ? <div style={{ textAlign: 'center', padding: '20px' }}><Spin /></div>
        : <ListaDocumentos documentos={documentos} modo="admin" onBorrar={borrarDoc} onPedirFirma={pedirFirma} />}
    </>
  )

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <div style={{ background: 'linear-gradient(135deg, #1B3A6B 0%, #2E5FA3 100%)', padding: isMobile ? '14px 14px' : '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
        <Title level={4} style={{ color: 'white', margin: 0, flex: 1 }}>Trabajadores</Title>
        <Button icon={<FilePdfOutlined />} onClick={() => setFragmentador(true)} disabled={trabajadores.length === 0} style={{ flexShrink: 0 }}>
          Subir nóminas
        </Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={abrirNuevo} style={{ background: '#2E7D32', flexShrink: 0 }}>
          {!isMobile && 'Nuevo'}
        </Button>
      </div>

      <FragmentadorNominas
        abierto={fragmentador}
        trabajadores={trabajadores}
        onCerrar={(huboCambios) => { setFragmentador(false); if (huboCambios) cargar() }}
      />

      <Drawer
        title={editando ? `${editando.nombre} ${editando.apellidos || ''}` : 'Nuevo trabajador'}
        open={abierto}
        onClose={cerrar}
        width={isMobile ? '100%' : 560}
        extra={editando?.usuario_id && (
          <Button onClick={go} title={`Entrar en la aplicación como ${editando.nombre}, para ver lo que ve él`}
            style={{ background: '#ef9f27', borderColor: '#ef9f27', color: '#fff', fontWeight: 700, letterSpacing: '1px' }}>
            GO!
          </Button>
        )}
      >
        {editando ? (
          <Tabs activeKey={tab} onChange={setTab} items={[
            { key: 'ficha', label: 'Ficha', children: fichaForm },
            { key: 'documentos', label: `Documentos (${documentos.length})`, children: documentosTab },
          ]} />
        ) : fichaForm}
      </Drawer>

      <div style={{ padding: '16px' }}>
        {cargando ? (
          <div style={{ textAlign: 'center', marginTop: '60px' }}><Spin size="large" /></div>
        ) : trabajadores.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">Aún no hay trabajadores. Pulsa «Nuevo» para dar de alta el primero.</Text>}
            style={{ marginTop: '60px' }}
          />
        ) : (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {trabajadores.map(t => (
              <Card key={t.id} size="small" hoverable onClick={() => abrirFicha(t)} style={{
                borderRadius: '12px',
                borderLeft: `3px solid ${t.activo ? '#52c41a' : '#ff4d4f'}`,
                opacity: t.activo ? 1 : 0.6,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <UserOutlined style={{ color: '#1B3A6B' }} />
                      <Text strong style={{ fontSize: '15px', color: '#1B3A6B' }}>{t.nombre} {t.apellidos}</Text>
                      {!t.activo && <Tag color="error" style={{ fontSize: '10px' }}>De baja</Tag>}
                      {t.num_sin_firmar > 0 && <Tag color="warning" style={{ fontSize: '10px' }}>{t.num_sin_firmar} sin firmar</Tag>}
                    </div>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                      {t.puesto || 'Sin puesto'}
                      {t.telefono && <> · <PhoneOutlined /> {t.telefono}</>}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>
                      {t.usuario_id ? <><LinkOutlined /> Entra como {t.usuario_nombre}</> : 'Sin acceso a la aplicación'}
                    </Text>
                  </div>
                  <Badge count={t.num_sin_ver} size="small" title="Documentos que aún no ha abierto" color="#faad14">
                    <Button size="small" icon={<FileTextOutlined />} onClick={(e) => { e.stopPropagation(); abrirFicha(t, 'documentos') }}>
                      {t.num_documentos}
                    </Button>
                  </Badge>
                </div>
              </Card>
            ))}
          </Space>
        )}
      </div>
    </div>
  )
}
